import api from "services/api";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  InputAdornment,
  Tabs,
  Tab,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { showErrorToast } from "utils/toastNotification";
import { createNewEmployee, fetchEmployees } from "store/features/company/employee/employeeActions";
import addEmployeeSvg from "assets/modals/add.svg";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import { fetchDesignations } from "store/features/company/userManagement/designation/designationActions";
import { selectDesignations } from "store/features/company/userManagement/designation/designationSelectors";
import { fetchRolePermissionsData } from "store/features/company/roleManagement/rolePermissionActions";
import { selectRolePermissionRoles } from "store/features/company/roleManagement/rolePermissionSelectors";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import { fetchAttendancePolicies } from "store/features/company/attendanceManagement/attendancePolicy/attendancePolicyActions";
import { selectAttendancePolicies } from "store/features/company/attendanceManagement/attendancePolicy/attendancePolicySelectors";
import useFormValidation from "hooks/useFormValidation";
import { employeeValidationRules } from "constants/validationRules";

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AddEmployeeModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);

  const [employeeCodeError, setEmployeeCodeError] = useState("");
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { errors, validateForm, validateField, resetErrors } =
    useFormValidation(employeeValidationRules);

  // Form state with all fields
  const [formData, setFormData] = useState({
    employee_code: "",
    department_id: "",
    designation_id: "",
    attendance_policy_id: "",
    role_id: "",
    reporting_manager_id: "",
    employment_type: "full_time",
    work_location: "",
    joining_date: "",

    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    gender: "",
    date_of_birth: "",
    blood_group: "",
    current_address: "",
    permanent_address: "",
    emergency_contact_no: "",

    bank_name: "",
    bank_account_number: "",
    ifsc_code: "",
    pan_number: "",
    AadhaarNo: "",
    uan_number: "",
    pf_number: "",
    esi_number: "",
    basic_salary: "",
    ctc: "",
  });

  const departments = useSelector(selectDepartments);
  const designations = useSelector(selectDesignations);
  const roles = useSelector(selectRolePermissionRoles);
  const employees = useSelector(selectEmployees);
  const attendancePolicies = useSelector(selectAttendancePolicies);

  // Helper functions for employee code generation
  const getRandomNumber = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const generateEmployeeCode = (firstName, lastName) => {
    if (!firstName) return "";

    const firstNamePart = firstName.substring(0, 3).toUpperCase();
    const lastNamePart = lastName ? lastName.substring(0, 2).toUpperCase() : "";
    const randomNum = getRandomNumber(1000, 9999);

    return `${firstNamePart}${lastNamePart}${randomNum}`;
  };

  const isEmployeeCodeUnique = async (code) => {
    if (!code) return true;
    try {
      const response = await api.get("v1/api/employees/list");
      if (response?.meta?.status === true) {
        return !response.data.find((emp) => emp.employee_code === code);
      }
      return true;
    } catch (error) {
      console.error("Error checking employee code:", error);
      return true;
    }
  };

  const generateUniqueEmployeeCode = async (firstName, lastName) => {
    let code = generateEmployeeCode(firstName, lastName);
    let attempts = 0;
    const maxAttempts = 10;

    while (!(await isEmployeeCodeUnique(code)) && attempts < maxAttempts) {
      const firstNamePart = firstName.substring(0, 3).toUpperCase();
      const lastNamePart = lastName
        ? lastName.substring(0, 2).toUpperCase()
        : "";
      const randomNum = getRandomNumber(1000, 9999);
      code = `${firstNamePart}${lastNamePart}${randomNum}`;
      attempts++;
    }

    return code;
  };

  // Fetch data on modal open
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDepartments());
      dispatch(fetchDesignations());
      dispatch(fetchRolePermissionsData());
      dispatch(fetchEmployees());
      dispatch(fetchAttendancePolicies());
    }
  }, [isOpen, dispatch]);

  // Handle body scroll
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Updated handleChange with auto-generation
  const handleChange = async (e) => {
    const { name, value } = e.target;
    const nextFormData = { ...formData, [name]: value };

    setFormData(nextFormData);
    validateField(name, value, nextFormData);

    // Auto-generate employee code
    if (name === "first_name" || name === "last_name") {
      const firstName = name === "first_name" ? value : formData.first_name;
      const lastName = name === "last_name" ? value : formData.last_name;

      if (firstName && firstName.length >= 2) {
        const currentCode = formData.employee_code;
        const isAutoGenerated =
          currentCode &&
          currentCode.startsWith(firstName.substring(0, 3).toUpperCase());

        if (!currentCode || isAutoGenerated) {
          const generatedCode = await generateUniqueEmployeeCode(
            firstName,
            lastName,
          );
          setFormData((prev) => ({
            ...prev,
            employee_code: generatedCode,
          }));
          validateField("employee_code", generatedCode, {
            ...nextFormData,
            employee_code: generatedCode,
          });
          // Check uniqueness
          checkEmployeeCode(generatedCode);
        }
      }
    }

    // Check employee code uniqueness when user manually types it
    if (name === "employee_code") {
      checkEmployeeCode(value);
    }
  };

  const handleDateChange = (name, value) => {
    const formattedValue = value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    const nextFormData = { ...formData, [name]: formattedValue };
    setFormData(nextFormData);
    validateField(name, formattedValue, nextFormData);
  };

  const checkEmployeeCode = async (code) => {
    if (!code || code.length < 3) {
      setEmployeeCodeError("");
      return;
    }

    setIsCheckingCode(true);
    try {
      const response = await api.get("v1/api/employees/list");
      if (response?.meta?.status === true) {
        const existingEmployee = response.data.find(
          (emp) => emp.employee_code === code,
        );
        if (existingEmployee) {
          setEmployeeCodeError("This employee code is already taken");
        } else {
          setEmployeeCodeError("");
        }
      }
    } catch (error) {
      console.error("Error checking employee code:", error);
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    console.log("HANDLE SUBMIT CALLED");
    e.preventDefault();

    if (!validateForm(formData)) {
      showErrorToast("Please fix the highlighted fields.");
      return;
    }

    if (employeeCodeError) {
      showErrorToast("Please use a different employee code");
      return;
    }

    setLoading(true);

    const formattedData = {
      employee_code: formData.employee_code,
      first_name: formData.first_name,
      email: formData.email,
      role_id: Number(formData.role_id),
      ...(formData.last_name && { last_name: formData.last_name }),
      ...(formData.phone && { phone: formData.phone }),
      ...(formData.gender && { gender: formData.gender }),
      ...(formData.date_of_birth && { date_of_birth: formData.date_of_birth }),
      ...(formData.joining_date && { joining_date: formData.joining_date }),
      ...(formData.department_id && {
        department_id: Number(formData.department_id),
      }),
      ...(formData.designation_id && {
        designation_id: Number(formData.designation_id),
      }),
      ...(formData.attendance_policy_id && {
        attendance_policy_id: Number(formData.attendance_policy_id),
      }),
      ...(formData.reporting_manager_id !== undefined && {
        reporting_manager_id: formData.reporting_manager_id ? Number(formData.reporting_manager_id) : null,
      }),
      ...(formData.employment_type && {
        employment_type: formData.employment_type,
      }),
      ...(formData.work_location && { work_location: formData.work_location }),
      ...(formData.bank_name && { bank_name: formData.bank_name }),
      ...(formData.bank_account_number && {
        bank_account_number: formData.bank_account_number,
      }),
      ...(formData.ifsc_code && { ifsc_code: formData.ifsc_code }),
      ...(formData.pan_number && { pan_number: formData.pan_number }),
      ...(formData.AadhaarNo && { AadhaarNo: formData.AadhaarNo }),
      ...(formData.uan_number && { uan_number: formData.uan_number }),
      ...(formData.pf_number && { pf_number: formData.pf_number }),
      ...(formData.esi_number && { esi_number: formData.esi_number }),
      ...(formData.blood_group && { blood_group: formData.blood_group }),
      ...(formData.current_address && {
        current_address: formData.current_address,
      }),
      ...(formData.permanent_address && {
        permanent_address: formData.permanent_address,
      }),
      ...(formData.emergency_contact_no && {
        emergency_contact_no: formData.emergency_contact_no,
      }),
      ...(formData.basic_salary && {
        basic_salary: Number(formData.basic_salary),
      }),
      ...(formData.ctc && { ctc: Number(formData.ctc) }),
    };

    const result = await dispatch(createNewEmployee(formattedData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      employee_code: "",
      department_id: "",
      designation_id: "",
      attendance_policy_id: "",
      role_id: "",
      reporting_manager_id: "",
      employment_type: "full_time",
      work_location: "",
      joining_date: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      gender: "male",
      date_of_birth: "",
      blood_group: "",
      current_address: "",
      permanent_address: "",
      emergency_contact_no: "",
      bank_name: "",
      bank_account_number: "",
      ifsc_code: "",
      pan_number: "",
      AadhaarNo: "",
      uan_number: "",
      pf_number: "",
      esi_number: "",
      basic_salary: "",
      ctc: "",
    });
    setTabValue(0);
    setEmployeeCodeError("");
    resetErrors();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleNext = (e) => {
    console.log("NEXT CLICKED");

    e.preventDefault();
    e.stopPropagation();

    if (tabValue < 2) {
      setTimeout(() => {
        setTabValue((prev) => prev + 1);
      }, 0);
    }
  };

  const handlePrevious = () => {
    if (tabValue > 0) {
      setTabValue(tabValue - 1);
    }
  };

  // Input styling
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "primary.main",
      },
    },
    "& .MuiInputLabel-asterisk": {
      color: "#ef4444",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
              .MuiPopover-root {
                z-index: 1400 !important;
              }
              .MuiPopper-root {
                z-index: 1400 !important;
              }
            `}
          </style>

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "700px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addEmployeeSvg}
                      alt="Add Employee"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Add Employee
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Add a new team member to your organization
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form
              onSubmit={(e) => {
                console.log("FORM SUBMITTED", e.nativeEvent.submitter);
                handleSubmit(e);
              }}
            // onSubmit={handleSubmit}
            >
              {/* Tabs */}
              <Box
                sx={{
                  position: "sticky",
                  top: "110px",
                  zIndex: 5,
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                  px: 3,
                  pt: 2,
                  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                    }`,
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "uppercase",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      fontStyle: "italic",
                      borderRadius: "8px",
                      minHeight: "44px",
                      color: "text.secondary",
                      "&.Mui-selected": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#f59e0b",
                      height: "3px",
                      borderRadius: "3px",
                    },
                  }}
                >
                  <Tab label="Personal Information" />
                  <Tab label="Work Information" />
                  <Tab label="Account Information" />
                </Tabs>
              </Box>
              <Box sx={{ px: 3 }}>
                {/* Personal Details Tab */}
                <TabPanel value={tabValue} index={0}>
                  <Stack spacing={3}>
                    {/* Name Fields */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        placeholder="Enter first name"
                        error={!!errors.first_name}
                        helperText={errors.first_name}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="last_name"
                        required
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Enter last name"
                        error={!!errors.last_name}
                        helperText={errors.last_name}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>

                    {/* Email and Phone */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="employee@company.com"
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
                          handleChange(e);
                        }}
                        required
                        placeholder="9876543210"
                        error={!!errors.phone}
                        helperText={errors.phone}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>

                    {/* Gender and Blood Group */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="Blood Group"
                        name="blood_group"
                        value={formData.blood_group}
                        onChange={handleChange}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="">Select Blood Group</MenuItem>
                        <MenuItem value="A+">A+</MenuItem>
                        <MenuItem value="A-">A-</MenuItem>
                        <MenuItem value="B+">B+</MenuItem>
                        <MenuItem value="B-">B-</MenuItem>
                        <MenuItem value="AB+">AB+</MenuItem>
                        <MenuItem value="AB-">AB-</MenuItem>
                        <MenuItem value="O+">O+</MenuItem>
                        <MenuItem value="O-">O-</MenuItem>
                      </TextField>
                    </Stack>

                    {/* Date of Birth and emergency contact number */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <DatePicker
                        label="Date of Birth"
                        value={formData.date_of_birth ? dayjs(formData.date_of_birth) : null}
                        onChange={(newValue) => handleDateChange("date_of_birth", newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            error: !!errors.date_of_birth,
                            helperText: errors.date_of_birth,
                            sx: inputSx,
                            InputLabelProps: { shrink: true }
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Emergency Contact Number"
                        name="emergency_contact_no"
                        value={formData.emergency_contact_no}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
                          handleChange(e);
                        }}
                        required
                        placeholder="9876543211"
                        error={!!errors.emergency_contact_no}
                        helperText={errors.emergency_contact_no}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>

                    {/* Addresses */}
                    <TextField
                      fullWidth
                      label="Current Address"
                      name="current_address"
                      value={formData.current_address}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      placeholder="Enter current address"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.current_address}
                      helperText={errors.current_address}
                    />
                    <TextField
                      fullWidth
                      label="Permanent Address"
                      name="permanent_address"
                      value={formData.permanent_address}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      placeholder="Enter permanent address"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.permanent_address}
                      helperText={errors.permanent_address}
                    />
                  </Stack>
                </TabPanel>

                {/* Work Details Tab */}
                <TabPanel value={tabValue} index={1}>
                  <Stack spacing={3}>
                    {/* Employee Code and employement type*/}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Employee Code"
                        name="employee_code"
                        value={formData.employee_code}
                        onChange={handleChange}
                        required
                        placeholder="e.g., EMP001"
                        error={!!employeeCodeError || !!errors.employee_code}
                        helperText={
                          employeeCodeError || errors.employee_code || "Auto-generated from name or enter manually"
                        }
                        InputProps={{
                          endAdornment: isCheckingCode && (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ),
                        }}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        select
                        label="Employment Type"
                        name="employment_type"
                        value={formData.employment_type}
                        onChange={handleChange}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="full_time">Full Time</MenuItem>
                        <MenuItem value="part_time">Part Time</MenuItem>
                        <MenuItem value="contract">Contract</MenuItem>
                        <MenuItem value="intern">Intern</MenuItem>
                        <MenuItem value="permanent">Permanent</MenuItem>
                      </TextField>
                    </Stack>

                    {/* Role - Mandatory */}
                    <TextField
                      fullWidth
                      select
                      label="Role"
                      name="role_id"
                      value={formData.role_id}
                      onChange={handleChange}
                      required
                      placeholder="Select role"
                      error={!!errors.role_id}
                      helperText={
                        errors.role_id ||
                        "Select the role for this employee (e.g., Admin, Manager, Employee)"
                      }
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Select Role</MenuItem>
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                          {role.is_system === 1 && " (System)"}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Department and Designation */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Department"
                        name="department_id"
                        value={formData.department_id}
                        onChange={handleChange}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="">Select Department</MenuItem>
                        {departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name} ({dept.code})
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        select
                        label="Designation"
                        name="designation_id"
                        value={formData.designation_id}
                        onChange={handleChange}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="">Select Designation</MenuItem>
                        {designations.map((desg) => (
                          <MenuItem key={desg.id} value={desg.id}>
                            {desg.name} ({desg.code})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>

                    {/* Reporting Manager */}
                    <TextField
                      fullWidth
                      select
                      label="Reporting Manager"
                      name="reporting_manager_id"
                      value={formData.reporting_manager_id}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Select Reporting Manager</MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_code})
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Shift / Attendance Policy */}
                    <TextField
                      fullWidth
                      select
                      label="Shift / Attendance Policy"
                      name="attendance_policy_id"
                      value={formData.attendance_policy_id}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Select Shift / Policy</MenuItem>
                      {attendancePolicies.map((policy) => (
                        <MenuItem key={policy.id} value={policy.id}>
                          {policy.name} {policy.shift_name ? `(${policy.shift_name}: ${policy.shift_start_time} - ${policy.shift_end_time})` : ""}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Joining Date and Work Location */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <DatePicker
                        label="Joining Date"
                        value={formData.joining_date ? dayjs(formData.joining_date) : null}
                        onChange={(newValue) => handleDateChange("joining_date", newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.joining_date,
                            helperText: errors.joining_date,
                            sx: inputSx,
                            InputLabelProps: { shrink: true }
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Work Location"
                        name="work_location"
                        value={formData.work_location}
                        onChange={handleChange}
                        placeholder="Delhi, Mumbai, etc."
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                  </Stack>
                </TabPanel>

                {/* Account Details Tab */}
                <TabPanel value={tabValue} index={2}>
                  <Stack spacing={3}>
                    {/* Bank Details */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        borderBottom: `2px solid #f59e0b`,
                        pb: 1,
                      }}
                    >
                      Bank Details
                    </Typography>

                    <TextField
                      fullWidth
                      label="Bank Name"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleChange}
                      placeholder="State Bank of India"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Bank Account Number"
                      name="bank_account_number"
                      value={formData.bank_account_number}
                      onChange={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, "").slice(0, 18);
                        handleChange(e);
                      }}
                      placeholder="1234567890"
                      error={!!errors.bank_account_number}
                      helperText={errors.bank_account_number}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      name="ifsc_code"
                      value={formData.ifsc_code}
                      onChange={(e) => {
                        e.target.value = e.target.value.toUpperCase();
                        handleChange(e);
                      }}
                      placeholder="SBIN0001234"
                      error={!!errors.ifsc_code}
                      helperText={errors.ifsc_code}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />

                    {/* Statutory Details */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        borderBottom: `2px solid #f59e0b`,
                        pb: 1,
                        mt: 2,
                      }}
                    >
                      Statutory Details
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="PAN Number"
                        name="pan_number"
                        value={formData.pan_number}
                        onChange={(e) => {
                          e.target.value = e.target.value.toUpperCase();
                          handleChange(e);
                        }}
                        placeholder="ABCDE1234F"
                        error={!!errors.pan_number}
                        helperText={errors.pan_number}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        label="Aadhaar Number"
                        name="AadhaarNo"
                        value={formData.AadhaarNo}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/\D/g, "").slice(0, 12);
                          handleChange(e);
                        }}
                        placeholder="123456789012"
                        error={!!errors.AadhaarNo}
                        helperText={errors.AadhaarNo}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="UAN Number"
                        name="uan_number"
                        value={formData.uan_number}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(/\D/g, "").slice(0, 12);
                          handleChange(e);
                        }}
                        placeholder="100200300400"
                        error={!!errors.uan_number}
                        helperText={errors.uan_number}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        fullWidth
                        label="PF Number"
                        name="pf_number"
                        value={formData.pf_number}
                        onChange={handleChange}
                        placeholder="PF12345"
                        error={!!errors.pf_number}
                        helperText={errors.pf_number}
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>

                    <TextField
                      fullWidth
                      label="ESI Number"
                      name="esi_number"
                      value={formData.esi_number}
                      onChange={handleChange}
                      placeholder="ESI12345"
                      error={!!errors.esi_number}
                      helperText={errors.esi_number}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                </TabPanel>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                    }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                {/* Left Button */}
                {tabValue === 0 ? (
                  <Button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor:
                        theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                      color:
                        theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    }}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handlePrevious}
                    disabled={loading}
                    fullWidth
                    variant="outlined"
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 500,
                      borderColor:
                        theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                      color:
                        theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    }}
                  >
                    Previous
                  </Button>
                )}

                {/* Right Button */}
                {tabValue < 2 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    fullWidth
                    variant="contained"
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 500,
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#fff",
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={loading}
                    fullWidth
                    sx={{
                      borderRadius: "12px",
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 500,
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#ffffff",
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                    ) : (
                      "Add Employee"
                    )}
                  </Button>
                )}
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddEmployeeModal;
